import { useCallback, useEffect, useRef, useState } from 'react';
import { BIOME_REGISTRY } from '../../data/biomes.config';
import {
  generateDungeon,
  generateTestArena,
} from '../../utils/dungeon-generator';
import { IDungeonGenerationResult } from '../../types/map.types';
import { useDevtoolStore } from '../../store/devtoolSlice';
import './DungeonGalleryScreen.scss';

const COLORS = {
  bg: '#0a0a10',
  ground: '#5a5a3a',
  wall: '#22221c',
  water: '#2a4a8b',
  corridor: '#3a6a8a',
  locationInteract: '#ffd24a',
  locationWalk: '#ff9933',
} as const;

const COUNT_OPTIONS = [4, 9, 16] as const;
const biomeIds = Object.keys(BIOME_REGISTRY);

const colsFor = (count: number) => (count <= 4 ? 2 : count <= 9 ? 3 : 4);
const thumbWidthFor = (count: number) =>
  count <= 4 ? 260 : count <= 9 ? 210 : 165;

function generateOne(biomeId: string): IDungeonGenerationResult {
  const biome = BIOME_REGISTRY[biomeId];
  if (biome.generation === 'test-arena') {
    return generateTestArena(biome.width, biome.height);
  }
  return generateDungeon({
    width: biome.width,
    height: biome.height,
    style: biome.style,
    roomCount: biome.roomCount,
    locations: biome.possibleLocations,
    roomTemplates: biome.roomTemplates ?? [],
  });
}

const roomColor = (index: number) => `hsl(${(index * 67) % 360} 80% 55%)`;

const GalleryThumb = ({
  result,
  px,
}: {
  result: IDungeonGenerationResult;
  px: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const { width, height, tiles, rooms, placedLocations } = result;
    canvas.width = width * px;
    canvas.height = height * px;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const at = (x: number, y: number) => (height - 1 - y) * px;
    for (const tile of tiles) {
      ctx.fillStyle =
        tile.type === 'ground'
          ? COLORS.ground
          : tile.type === 'water'
            ? COLORS.water
            : COLORS.wall;
      ctx.fillRect(tile.x * px, at(tile.x, tile.y), px, px);
    }

    const roomKeys = new Set<string>();
    ctx.globalAlpha = 0.45;
    rooms.forEach((room, index) => {
      ctx.fillStyle = roomColor(index);
      for (const c of room.cells) {
        roomKeys.add(`${c.x},${c.y}`);
        ctx.fillRect(c.x * px, at(c.x, c.y), px, px);
      }
    });
    ctx.globalAlpha = 1;

    // Ground cells in no room are corridors.
    ctx.fillStyle = COLORS.corridor;
    for (const tile of tiles) {
      if (tile.type === 'ground' && !roomKeys.has(`${tile.x},${tile.y}`)) {
        ctx.fillRect(tile.x * px, at(tile.x, tile.y), px, px);
      }
    }

    for (const loc of placedLocations) {
      ctx.fillStyle =
        loc.interactionType === 'walk'
          ? COLORS.locationWalk
          : COLORS.locationInteract;
      ctx.fillRect(
        loc.x * px,
        at(loc.x, loc.y),
        Math.max(px, 3),
        Math.max(px, 3),
      );
    }
  }, [result, px]);

  return <canvas ref={canvasRef} className="gallery-thumb-canvas" />;
};

const caption = (result: IDungeonGenerationResult): string => {
  const d = result.debug;
  if (!d) {
    return `${result.rooms.length} room(s)`;
  }
  return `[${d.style}] ${d.rooms.length} rooms · placed ${d.placedCount} · dropped ${d.droppedCount} · ${d.corridorCount} corr`;
};

export const DungeonGalleryScreen = () => {
  const setGalleryOpen = useDevtoolStore((s) => s.setGalleryOpen);
  const [biomeIndex, setBiomeIndex] = useState(0);
  const [count, setCount] = useState<number>(9);
  const [results, setResults] = useState<IDungeonGenerationResult[]>([]);

  const biomeId = biomeIds[biomeIndex];
  const biome = BIOME_REGISTRY[biomeId];

  const regenerate = useCallback(() => {
    setResults(Array.from({ length: count }, () => generateOne(biomeId)));
  }, [count, biomeId]);

  useEffect(() => {
    regenerate();
  }, [regenerate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        setGalleryOpen(false);
      } else if (e.code === 'KeyR') {
        regenerate();
      } else if (e.code === 'ArrowRight') {
        setBiomeIndex((i) => (i + 1) % biomeIds.length);
      } else if (e.code === 'ArrowLeft') {
        setBiomeIndex((i) => (i - 1 + biomeIds.length) % biomeIds.length);
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [setGalleryOpen, regenerate]);

  const px = Math.max(2, Math.floor(thumbWidthFor(count) / biome.width));

  return (
    <div className="gallery-screen">
      <div className="gallery-toolbar">
        <button
          className="gallery-btn"
          onClick={() =>
            setBiomeIndex((i) => (i - 1 + biomeIds.length) % biomeIds.length)
          }
        >
          ◀
        </button>
        <select
          className="gallery-select"
          value={biomeId}
          onChange={(e) => setBiomeIndex(biomeIds.indexOf(e.target.value))}
        >
          {biomeIds.map((id) => (
            <option key={id} value={id}>
              {BIOME_REGISTRY[id].label} ({id})
            </option>
          ))}
        </select>
        <button
          className="gallery-btn"
          onClick={() => setBiomeIndex((i) => (i + 1) % biomeIds.length)}
        >
          ▶
        </button>

        <span className="gallery-meta">
          {biome.style} · {biome.width}×{biome.height} · rooms{' '}
          {biome.roomCount[0]}–{biome.roomCount[1]}
        </span>

        <span className="gallery-spacer" />

        {COUNT_OPTIONS.map((c) => (
          <button
            key={c}
            className={`gallery-btn${c === count ? ' gallery-btn--active' : ''}`}
            onClick={() => setCount(c)}
          >
            {c}
          </button>
        ))}
        <button className="gallery-btn" onClick={regenerate}>
          Regénérer (R)
        </button>
        <button
          className="gallery-btn gallery-btn--close"
          onClick={() => setGalleryOpen(false)}
        >
          Fermer (Échap)
        </button>
      </div>

      <div
        className="gallery-grid"
        style={{
          gridTemplateColumns: `repeat(${colsFor(count)}, max-content)`,
        }}
      >
        {results.map((result, i) => (
          <div className="gallery-cell" key={i}>
            <GalleryThumb result={result} px={px} />
            <div className="gallery-caption">{caption(result)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
